import { googleLogout } from "@react-oauth/google";
import { ChevronsLeft, ChevronsRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Tooltip } from "react-tooltip";

import CustomLink from "../../../../components/CustomLink";
import { useAuth } from "../../../../context/Auth/AuthContext";

interface SidebarProps {
	onToggle: () => void;
	hideSidebar: boolean;
	isCollapsed: boolean;
}

const Sidebar = ({ onToggle, hideSidebar, isCollapsed }: SidebarProps) => {
	const { userData, logout } = useAuth();

	const { firstName } = userData ? userData : { firstName: "" };
	const collapsed = isCollapsed || hideSidebar;

	const storeNavigationFlow = (clear: boolean, shortUrl: string) => {
		const updatedNavFlowData = clear ? { clickedPage: shortUrl } : { clickedPage: "" };
		localStorage.setItem(`navFlowData${firstName}`, JSON.stringify(updatedNavFlowData));
	};

	const navItems = [
		{ to: "/job-market", icon: "fas fa-folder", label: "Jobs", onClick: () => storeNavigationFlow(true, "dashboard") },
		{
			to: "/job-market/post",
			icon: "fas fa-folder-plus",
			label: collapsed ? "Post Job" : "Post Job without JD",
			onClick: () => storeNavigationFlow(true, "job-market/post"),
		},
		{
			to: "/job-market/companies",
			icon: "fa-regular fa-building",
			label: "Companies",
			onClick: () => storeNavigationFlow(true, "job-market/companies"),
		},
		{ to: "/job-market/jds", icon: "fas fa-file-alt", label: "Job Descriptions" },
	];

	if (!collapsed) {
		navItems.push({ to: "/job-market/shared-leads", icon: "fa-solid fa-users-between-lines", label: "Shared Leads" });
	}

	return (
		<div className={collapsed ? "z-[999] w-[70px] -ml-2.5 -mt-5 overflow-hidden" : "z-[999] -ml-10"}>
			<Link to="/" className={collapsed ? "ml-0" : ""}>
				<img
					src="/images/logo.png"
					alt="Logo"
					className={
						collapsed ? "ml-[7px] h-[50px] w-[50px] object-contain" : "-ml-2.5 h-[50px] w-[100px] object-contain"
					}
				/>
			</Link>

			<ul className={collapsed ? "p-0" : "ml-[5px]"}>
				{navItems.map((item) => (
					<CustomLink key={item.to} to={item.to} onClick={item.onClick}>
						<li
							data-tooltip-id="sidebar-tooltip"
							data-tooltip-content={item.label}
							className={`w-full hover:bg-gray-200 ${
								collapsed ? "m-0 flex items-center justify-center border-b border-gray-200 py-2" : ""
							}`}
						>
							<i className={`${item.icon} mr-1.5 text-xs text-gray-500`} />
							{!collapsed && <span className="font-semibold text-black">{item.label}</span>}
						</li>
					</CustomLink>
				))}
			</ul>

			<ul className="absolute bottom-0 left-0 w-full" style={{ height: 80 }}>
				<ul
					className={
						collapsed ? "absolute bottom-5 left-0 w-full rounded bg-gray-200" : "absolute bottom-5 left-0 w-full"
					}
				>
					<CustomLink
						to="/signin"
						onClick={() => {
							logout();
							googleLogout();
						}}
						className="w-full"
					>
						<li data-tooltip-id="sidebar-tooltip" data-tooltip-content="Log Out" className="w-full hover:bg-gray-200">
							<i className="fas fa-sign-out-alt mr-1.5 text-xs text-gray-500" />
							{!collapsed && <span className="font-semibold text-black">Log Out</span>}
						</li>
					</CustomLink>
					<li
						data-tooltip-id="sidebar-tooltip"
						data-tooltip-content={collapsed ? "Expand" : "Collapse"}
						onClick={onToggle}
						className="flex cursor-pointer items-center justify-center py-2 hover:bg-gray-200"
					>
						{collapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
					</li>
				</ul>
			</ul>
			<Tooltip id="sidebar-tooltip" style={{ zIndex: 1000, position: "fixed" }} />
		</div>
	);
};

export default Sidebar;
